import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { faBell } from "@fortawesome/free-solid-svg-icons";
import Breadcrumbs from "../BreadCrum/BreadCrum";
import PageHeadingSection from "../pageheading/PageHeadingSection";
import CustomSelect from "../formfields/CustomSelect";
import apiClient from "../../../services/apiClient";
import { emergencyApi, readSession, apiMessage } from "./emergencyApi";

const scopes = [
    { value: "site", label: "Whole site" },
    { value: "wing", label: "Wing" },
    { value: "floor", label: "Floor" },
    { value: "unit", label: "Unit" }
];

const audiences = [
    { value: "admins", label: "Only admins" },
    { value: "all", label: "All users" }
];

async function firstList(requests) {
    for (const request of requests) {
        try {
            const response = await request();
            const body = response.data?.data || response.data?.Data || response.data;
            if (Array.isArray(body) && body.length) return body;
        } catch {
            // Try the next tenant route.
        }
    }
    return [];
}

const RaiseEmergency = () => {
    const { user } = useSelector((state) => state.auth);
    const { siteId, userId, isSiteAdmin } = readSession(user);
    const [types, setTypes] = useState([]);
    const [type, setType] = useState(null);
    const [scope, setScope] = useState(scopes[0]);
    const [audience, setAudience] = useState(audiences[1]);
    const [wings, setWings] = useState([]);
    const [floors, setFloors] = useState([]);
    const [units, setUnits] = useState([]);
    const [wing, setWing] = useState(null);
    const [floor, setFloor] = useState(null);
    const [unit, setUnit] = useState(null);
    const [message, setMessage] = useState("");
    const [location, setLocation] = useState({ latitude: null, longitude: null, locationText: "" });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        emergencyApi.getTypes(siteId).then((response) => {
            const list = response.data?.data || [];
            setTypes(list.map((item) => ({ value: item.id, label: item.name })));
        }).catch(() => toast.error("Failed to load emergency types"));
    }, [siteId]);

    useEffect(() => {
        firstList([
            () => apiClient.get("/WingMaster/GetAllWing"),
            () => apiClient.get(`/Residential/ResWingMaster/GetWingMasterBySiteId/${siteId}`)
        ]).then((list) => setWings(list.map((item) => ({
            value: item.wingId || item.WingId || item.id || item.Id,
            label: item.wingName || item.WingName || item.name || `Wing ${item.wingId || item.id}`
        })).filter((item) => item.value)));
        firstList([
            () => apiClient.get(`/UnitMaster/GetRecordBySiteId/${siteId}`),
            () => apiClient.get(`/Residential/ResUnitMaster/GetRecordBySiteId/${siteId}`)
        ]).then((list) => setUnits(list.map((item) => ({
            value: item.unitId || item.UnitId || item.id || item.Id,
            label: item.unitName || item.UnitName || item.unitNumber || `Unit ${item.unitId || item.id}`
        })).filter((item) => item.value)));
    }, [siteId]);

    useEffect(() => {
        if (!wing?.value) {
            setFloors([]);
            return;
        }
        firstList([
            () => apiClient.get("/FloorMaster/GetFloorByWingSiteId", { params: { WingId: wing.value, SiteId: siteId } }),
            () => apiClient.get("/Residential/ResFloorMaster/GetFloorByWingSiteId", { params: { WingId: wing.value, SiteId: siteId } })
        ]).then((list) => setFloors(list.map((item) => ({
            value: item.floorId || item.FloorId || item.id || item.Id,
            label: item.floorName || item.FloorName || item.name || `Floor ${item.floorId || item.id}`
        })).filter((item) => item.value)));
    }, [wing, siteId]);

    const captureLocation = () => {
        if (!navigator.geolocation) {
            toast.error("This browser cannot read the current location");
            return;
        }
        navigator.geolocation.getCurrentPosition((position) => {
            const latitude = position.coords.latitude;
            const longitude = position.coords.longitude;
            setLocation({
                latitude,
                longitude,
                locationText: `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`
            });
        }, () => toast.error("Location permission was denied"));
    };

    const raise = async () => {
        if (!isSiteAdmin) return;
        if (!type?.value) {
            toast.error("Select an emergency type");
            return;
        }
        if (scope.value === "wing" && !wing?.value) {
            toast.error("Select a wing");
            return;
        }
        if (scope.value === "floor" && !floor?.value) {
            toast.error("Select a floor");
            return;
        }
        if (scope.value === "unit" && !unit?.value) {
            toast.error("Select a unit");
            return;
        }
        setSaving(true);
        try {
            const response = await emergencyApi.raiseAdmin({
                userId,
                emergencyTypeId: Number(type.value),
                scope: scope.value,
                audience: audience?.value || "all",
                siteId,
                wingId: scope.value === "wing" || scope.value === "floor" ? Number(wing?.value || 0) : null,
                floorId: scope.value === "floor" ? Number(floor?.value || 0) : null,
                unitId: scope.value === "unit" ? Number(unit?.value || 0) : null,
                message,
                latitude: location.latitude,
                longitude: location.longitude,
                locationText: location.locationText
            });
            if (response.data?.isSuccess === false) {
                toast.error(response.data?.message || "Failed to raise the emergency");
                return;
            }
            toast.success("Emergency raised");
            setMessage("");
        } catch (error) {
            toast.error(apiMessage(error, "Failed to raise the emergency"));
        } finally {
            setSaving(false);
        }
    };

    if (!isSiteAdmin) {
        return (
            <div className="bg-dashboardbg px-6 py-4 border border-bordercolor rounded-lg">
                <Breadcrumbs items={["Emergency Services", "Raise"]} />
                <PageHeadingSection mainHeading="Raise emergency" subHeading="This page is available to a site admin." />
            </div>
        );
    }

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <Breadcrumbs items={["Emergency Services", "Raise"]} />
            <PageHeadingSection
                mainHeading="Raise emergency"
                subHeading="Alert the users in a site, wing, floor, or unit. The phone opens a full-screen alert."
                primaryBtnText={saving ? "Sending" : "Raise"}
                primaryBtnIcon={faBell}
                primaryBtnDisabled={saving}
                PrimaryOnClick={raise}
            />
            <div className="mt-4 grid gap-4 max-w-xl">
                <CustomSelect label="Type" options={types} value={type} onChange={setType} placeholder="Select a type" />
                <CustomSelect label="Scope" options={scopes} value={scope} onChange={(option) => { setScope(option); setWing(null); setFloor(null); setUnit(null); }} />
                <CustomSelect label="Notify" options={audiences} value={audience} onChange={setAudience} placeholder="Who should be notified" />
                {(scope.value === "wing" || scope.value === "floor") && (
                    <CustomSelect label="Wing" options={wings} value={wing} onChange={setWing} placeholder="Select a wing" />
                )}
                {scope.value === "floor" && (
                    <CustomSelect label="Floor" options={floors} value={floor} onChange={setFloor} placeholder="Select a floor" />
                )}
                {scope.value === "unit" && (
                    <CustomSelect label="Unit" options={units} value={unit} onChange={setUnit} placeholder="Select a unit" />
                )}
                <label className="text-xs font-semibold text-textprimary">
                    Message
                    <textarea className="mt-1 w-full border border-bordercolor rounded-md px-3 py-2 text-sm" rows={3} value={message} onChange={(event) => setMessage(event.target.value)} />
                </label>
                <div className="flex items-center gap-3">
                    <button type="button" className="border border-bordercolor rounded-md px-3 py-2 text-xs" onClick={captureLocation}>Use current location</button>
                    <span className="text-xs text-textsecondary">{location.locationText || "Location not captured"}</span>
                </div>
            </div>
        </div>
    );
};

export default RaiseEmergency;
